import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getRealEstate } from "../lib/api";
import type { RealEstateItem } from "../types/hygraph";
import Header from "../components/header";
import Footer from "../components/footer";
import ImageGallery from "../components/ImageGallery";

type EstateType = "all" | "developer" | "agency";

const CooperationsPage = () => {
    const [searchParams] = useSearchParams();
    const [realEstate, setRealEstate] = useState<RealEstateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState<EstateType>("all");
    const [displayLimit, setDisplayLimit] = useState(6);
    const [hasMore, setHasMore] = useState(false);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    // Read type from URL parameter on mount
    useEffect(() => {
        const typeParam = searchParams.get("type");
        if (typeParam === "developer" || typeParam === "agency") {
            setSelectedType(typeParam);
            // Scroll to gallery after a short delay to ensure content is loaded
            setTimeout(() => {
                const galleryElement =
                    document.getElementById("gallery-section");
                if (galleryElement) {
                    galleryElement.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }, 500);
        }
    }, [searchParams]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch all items (Hygraph pagination limit is usually 100)
                const realEstateData = await getRealEstate(0, 100);
                setRealEstate(realEstateData);
            } catch (error) {
                console.error("Error loading cooperation data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Filter by type
    const developers = realEstate.filter(
        (item) => item.estate_type === "developer"
    );
    const agencies = realEstate.filter((item) => item.estate_type === "agency");

    // Get displayed items based on selected type
    const getDisplayedItems = () => {
        let items: RealEstateItem[] = [];
        if (selectedType === "developer") {
            items = developers;
        } else if (selectedType === "agency") {
            items = agencies;
        } else {
            items = [...developers, ...agencies];
        }
        return items;
    };

    const displayedItems = getDisplayedItems();
    const visibleItems = displayedItems.slice(0, displayLimit);
    const remainingCount = displayedItems.length - displayLimit;

    // Update hasMore when type or limit changes
    useEffect(() => {
        setHasMore(displayedItems.length > displayLimit);
    }, [selectedType, displayLimit, displayedItems.length]);

    // Reset display limit when type changes
    useEffect(() => {
        setDisplayLimit(6);
    }, [selectedType]);

    const handleLoadMore = () => {
        setDisplayLimit((prev) => prev + 6);
    };

    const handleTypeChange = (type: EstateType) => {
        setSelectedType(type);
        // Scroll to gallery section
        setTimeout(() => {
            const galleryElement = document.getElementById("gallery-section");
            if (galleryElement) {
                galleryElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }, 100);
    };

    return (
        <>
            <Helmet>
                <title>
                    Immobilienkooperationen - DeutschAssist | Karolina Zielinska
                </title>
                <meta
                    name="description"
                    content="Zusammenarbeit mit vertrauenswürdigen Entwicklern und Immobilienagenturen in Westpommern. Verifizierte Investitionen und beste Angebote."
                />
                <meta
                    property="og:title"
                    content="Immobilienkooperationen - DeutschAssist"
                />
                <meta
                    property="og:url"
                    content="https://karolinazielinska.com/kooperationen"
                />
                <link
                    rel="canonical"
                    href="https://karolinazielinska.com/kooperationen"
                />
            </Helmet>
            <Header />
            <section
                className="custom-screen py-16 min-h-screen"
                id="kooperationen"
            >
                <div className="w-full h-px bg-text/20 mb-8" />

                <motion.div
                    className="text-center flex flex-col items-center mb-12"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1 className="text-2xl md:text-4xl mb-4">
                        Zusammenarbeit mit Entwicklern und Immobilienagenturen
                    </h1>
                    <div className="max-w-[68ch] space-y-4 text-left md:text-center">
                        <p className="text-lg">
                            Ich arbeite mit vertrauenswürdigen Entwicklern und
                            renommierten Immobilienagenturen in der Woiwodschaft
                            Westpommern zusammen.
                        </p>
                        <p>
                            Dies verschafft Ihnen Zugang zu verifizierten
                            Investitionen und den besten Angeboten, und ich
                            übernehme persönlich die gesamte Kontakt- und
                            Unterstützungsarbeit für Sie – von der Präsentation
                            bis zur Finalisierung des Kaufs.
                        </p>
                        <p className="font-semibold">
                            Der Zugang zu allen Angeboten und der Kontakt mit
                            Partnern erfolgt ausschließlich über mich.
                        </p>
                    </div>
                </motion.div>

                {loading ? (
                    <div className="text-center py-16">
                        <p>Laden...</p>
                    </div>
                ) : (
                    <>
                        {/* Type Navigation Buttons */}
                        {realEstate.length > 0 && (
                            <motion.div
                                className="flex flex-wrap justify-center gap-4 mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <button
                                    onClick={() => handleTypeChange("all")}
                                    className={`px-6 py-3 rounded-full border transition-all ${
                                        selectedType === "all"
                                            ? "bg-primary text-bg border-primary"
                                            : "bg-transparent text-text border-text/20 hover:border-text"
                                    }`}
                                >
                                    Alle ({realEstate.length})
                                </button>
                                {developers.length > 0 && (
                                    <button
                                        onClick={() =>
                                            handleTypeChange("developer")
                                        }
                                        className={`px-6 py-3 rounded-full border transition-all ${
                                            selectedType === "developer"
                                                ? "bg-primary text-bg border-primary"
                                                : "bg-transparent text-text border-text/20 hover:border-text"
                                        }`}
                                    >
                                        Entwickler ({developers.length})
                                    </button>
                                )}
                                {agencies.length > 0 && (
                                    <button
                                        onClick={() =>
                                            handleTypeChange("agency")
                                        }
                                        className={`px-6 py-3 rounded-full border transition-all ${
                                            selectedType === "agency"
                                                ? "bg-primary text-bg border-primary"
                                                : "bg-transparent text-text border-text/20 hover:border-text"
                                        }`}
                                    >
                                        Immobilienagenturen ({agencies.length})
                                    </button>
                                )}
                            </motion.div>
                        )}

                        {/* Real Estate Gallery */}
                        {realEstate.length > 0 && (
                            <div id="gallery-section">
                                <h2 className="text-xl md:text-2xl mb-8 text-center">
                                    Immobiliengalerie
                                </h2>

                                {displayedItems.length === 0 ? (
                                    <div className="text-center py-16">
                                        <p className="text-text/60">
                                            Keine Einträge in dieser Kategorie.
                                        </p>
                                    </div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, y: 50 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8 }}
                                        viewport={{ once: true }}
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {visibleItems.map((item, idx) => (
                                                <motion.article
                                                    key={item.id}
                                                    className="overflow-hidden rounded-lg border border-text/20 hover:border-primary transition-colors"
                                                    initial={{
                                                        opacity: 0,
                                                        y: 30,
                                                    }}
                                                    whileInView={{
                                                        opacity: 1,
                                                        y: 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.5,
                                                        delay: idx * 0.05,
                                                    }}
                                                    viewport={{ once: true }}
                                                >
                                                    <ImageGallery
                                                        images={item.images}
                                                        title={item.title}
                                                    />
                                                    <div className="p-4">
                                                        <h4 className="font-bold mb-2 text-lg">
                                                            {item.title}
                                                        </h4>
                                                        <div className="text-sm text-text/80 prose prose-sm max-w-none prose-headings:text-text prose-p:text-text/80 prose-a:text-primary prose-strong:text-text whitespace-pre-line">
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {item.description}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </motion.article>
                                            ))}
                                        </div>

                                        {/* Load More Button */}
                                        {hasMore && (
                                            <motion.div
                                                className="flex justify-center mt-8"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.3 }}
                                            >
                                                <button
                                                    onClick={handleLoadMore}
                                                    className="px-6 py-3 bg-primary text-bg rounded-full hover:bg-primary/90 transition-colors"
                                                >
                                                    Mehr laden ({remainingCount}{" "}
                                                    weitere)
                                                </button>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}
                            </div>
                        )}
                    </>
                )}

                <div className="w-full h-px bg-text/20 mt-16" />
            </section>
            <Footer />
        </>
    );
};

export default CooperationsPage;
